import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Image as ImageIcon, Mail, MessageSquare, Sparkles, Save, CheckCircle2, AlertCircle, Video, CreditCard, MapPinned, Fingerprint, MapPin, Loader2, Database, Download, RotateCcw, Trash2, MessageCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import MessageTemplatesTab from "./settings/MessageTemplatesTab";
import AiProviderCard from "./settings/AiProviderCard";

const WA_NOTIFY_TYPES = [
  { key: "attendance", label: "الحضور والغياب" },
  { key: "grades", label: "الدرجات" },
  { key: "invoices", label: "الفواتير والمستحقات" },
  { key: "behavior", label: "ملاحظات السلوك" },
  { key: "transport_boarding", label: "صعود ونزول الحافلة" },
  { key: "trip_started", label: "انطلاق الحافلة" },
  { key: "daily_summary", label: "التلخيص الذكي اليومي (مساءً)" },
];

export default function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // School geofence state — independent of app_settings (lives on /api/schools/{id})
  const [school, setSchool] = useState(null);
  const [schoolForm, setSchoolForm] = useState({});
  const [savingSchool, setSavingSchool] = useState(false);
  const [locating, setLocating] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);

  // SMS provider catalog (loaded on mount)
  const [smsProviders, setSmsProviders] = useState([]);
  const [testSmsTo, setTestSmsTo] = useState("");
  const [testingSms, setTestingSms] = useState(false);

  // WhatsApp provider catalog (Iter45)
  const [waProviders, setWaProviders] = useState([]);
  const [testWaTo, setTestWaTo] = useState("");
  const [testingWa, setTestingWa] = useState(false);

  // Danger zone — full data reset + granular cleanups
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [keepSchools, setKeepSchools] = useState(true);
  const [keepUploadedFiles, setKeepUploadedFiles] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/users").then(r => setAllUsers(r.data || [])).catch(() => {});
    }
  }, [user?.role]);
  const reloadUsers = () => api.get("/users").then(r => setAllUsers(r.data || [])).catch(() => {});

  // ------- Backups (super_admin only) -------
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const loadBackups = () => api.get("/admin/backups").then(r => setBackups(r.data || [])).catch(() => {});
  useEffect(() => {
    if (user?.role === "super_admin") {
      loadBackups();
      api.get("/admin/backups/status").then(r => setBackupStatus(r.data || null)).catch(() => {});
    }
  }, [user?.role]);

  const createBackupNow = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/admin/backups/now");
      const cloudMsg = data.s3_uploaded ? ` · ${t("set_backup_uploaded")}` : "";
      toast.success(`✅ ${t("backup_now")} (${data.total_documents} ${t("set_backup_records")} · ${(data.size_bytes/1024).toFixed(1)} KB)${cloudMsg}`);
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || t("set_backup_created_fail")); }
    setBackupBusy(false);
  };
  const restoreBackup = async (b) => {
    if (!window.confirm(t("backup_restore_warning"))) return;
    setBackupBusy(true);
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`);
      toast.success(`✅ ${t("backup_restore_ok")} (${data.restored_documents} ${t("set_backup_records")} · ${data.restored_collections} ${t("set_collections_count")})`);
    } catch (e) { toast.error(e.response?.data?.detail || t("set_backup_restore_fail")); }
    setBackupBusy(false);
  };
  const downloadBackup = async (b) => {
    try {
      const resp = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/gzip" }));
      const a = document.createElement("a");
      a.href = url; a.download = b.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch { toast.error(t("set_backup_download_fail")); }
  };
  const deleteBackup = async (b) => {
    if (!window.confirm(`${t("set_backup_delete_confirm")} ${b.filename}?`)) return;
    try {
      await api.delete(`/admin/backups/${b.id}`);
      toast.success(t("set_backup_deleted"));
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || t("set_action_failed")); }
  };
  // ------- /Backups -------

  const performReset = async () => {
    if (resetPhrase !== "RESET ALL DATA") { toast.error(t("set_reset_phrase_error")); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", {
        confirm_phrase: resetPhrase,
        keep_users: true,
        keep_schools: keepSchools,
        keep_settings: true,
        keep_departments: true,
        keep_uploaded_files: keepUploadedFiles,
      });
      const total = Object.values(data.deleted || {}).filter(v => typeof v === "number").reduce((a, b) => a + b, 0);
      toast.success(`✅ ${t("set_reset_done")} ${total} ${t("set_reset_from")} ${Object.keys(data.deleted || {}).length} ${t("set_reset_data_collections")}`);
      setResetPhrase("");
    } catch (e) { toast.error(e.response?.data?.detail || t("set_reset_failed")); }
    setResetting(false);
  };

  const cleanupDemoUsers = async () => {
    if (!window.confirm(t("set_cleanup_demo_confirm"))) return;
    try {
      const { data } = await api.post("/admin/cleanup-demo-users");
      toast.success(`✅ ${t("set_cleanup_done")} ${data.deleted_users} · ${t("set_remaining")} ${data.remaining_super_admins} ${t("set_super_admin")}`);
      reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("set_action_failed")); }
  };
  const deleteOneUser = async (u) => {
    if (!window.confirm(`${t("set_del_user_confirm_a")} ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${t("set_user_deleted")} ${u.email}`);
      reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t("set_action_failed")); }
  };
  const clearAuditLogs = async () => {
    if (!window.confirm(t("set_audit_clear_confirm"))) return;
    try {
      const { data } = await api.post("/admin/clear-audit-logs");
      toast.success(`✅ ${t("set_audit_cleared")} ${data.deleted} ${t("set_audit_records")}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("set_action_failed")); }
  };
  const clearTransportLogs = async () => {
    if (!window.confirm(t("set_transport_logs_confirm"))) return;
    try {
      const { data } = await api.post("/admin/clear-transport-logs");
      toast.success(`✅ ${t("set_transport_logs_done")} ${data.total} ${t("set_transport_reports_lbl")} ${Object.keys(data.details).length} ${t("set_collections_count")}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("set_action_failed")); }
  };

  const load = () => api.get("/settings").then((r) => { setData(r.data); setForm({}); });
  const loadSchool = () =>
    api.get("/schools").then((r) => {
      const list = r.data || [];
      if (list.length > 0) { setSchool(list[0]); setSchoolForm({}); }
    }).catch(() => {});
  useEffect(() => { load(); loadSchool(); }, []);
  useEffect(() => {
    api.get("/settings/sms-providers").then(r => setSmsProviders(r.data?.providers || [])).catch(() => {});
    api.get("/settings/whatsapp-providers").then(r => setWaProviders(r.data?.providers || [])).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">...</div>;

  const merge = (patch) => setForm((f) => ({ ...f, ...patch }));
  const v = (key) => (form[key] !== undefined ? form[key] : (data[key] ?? ""));

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      merge({ site_logo_id: up.id });
      toast.success(t("success"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success(t("success"));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const sv = (key) => (schoolForm[key] !== undefined ? schoolForm[key] : (school?.[key] ?? ""));
  const mergeSchool = (patch) => setSchoolForm((f) => ({ ...f, ...patch }));

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error(t("geolocation_unsupported")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mergeSchool({
          geo_lat: Number(pos.coords.latitude.toFixed(6)),
          geo_lng: Number(pos.coords.longitude.toFixed(6)),
        });
        setLocating(false);
        toast.success(t("success"));
      },
      (err) => {
        toast.error(err.code === 1 ? t("geolocation_denied") : err.message || "Error");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const saveSchool = async () => {
    if (!school) return;
    setSavingSchool(true);
    try {
      const payload = { ...schoolForm };
      // Numeric coercion
      if (payload.geo_lat !== undefined && payload.geo_lat !== "") payload.geo_lat = Number(payload.geo_lat);
      if (payload.geo_lng !== undefined && payload.geo_lng !== "") payload.geo_lng = Number(payload.geo_lng);
      if (payload.geo_radius_m !== undefined && payload.geo_radius_m !== "") payload.geo_radius_m = Number(payload.geo_radius_m);
      await api.put(`/schools/${school.id}`, payload);
      toast.success(t("success"));
      await loadSchool();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSavingSchool(false);
  };

  const sendTestEmail = async () => {
    if (!testEmailTo || !testEmailTo.includes("@")) { toast.error(t("set_invalid_email")); return; }
    setTestingEmail(true);
    try {
      const { data } = await api.post("/settings/test-email", { to_email: testEmailTo });
      if (data.ok) toast.success(`✅ ${t("set_sent_via")} (${data.provider})`);
      else toast.error(`${t("set_send_failed")} (${data.provider}): ${data.error || data.status}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setTestingEmail(false);
  };

  const sendTestSms = async () => {
    if (!testSmsTo || testSmsTo.length < 7) { toast.error(t("set_invalid_phone")); return; }
    setTestingSms(true);
    try {
      const { data } = await api.post("/settings/test-sms", { to_phone: testSmsTo });
      if (data.ok) toast.success(`✅ ${t("set_sent_via_provider")} ${data.provider}`);
      else toast.error(`${t("set_send_failed")} (${data.provider}): ${data.error || data.status}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setTestingSms(false);
  };

  const sendTestWhatsapp = async () => {
    if (!testWaTo || testWaTo.length < 7) { toast.error(t("set_invalid_phone")); return; }
    setTestingWa(true);
    try {
      const { data } = await api.post("/settings/test-whatsapp", { to_phone: testWaTo });
      if (data.ok) toast.success(`✅ تم الإرسال عبر ${data.provider}`);
      else toast.error(`فشل الإرسال (${data.provider}): ${data.error || data.status}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setTestingWa(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-end">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("settings")}</h1>
          <p className="text-sm text-muted-foreground mt-2">SCHOOLIN AI · سكول إن</p>
        </div>
        <Button onClick={save} disabled={saving || Object.keys(form).length === 0} data-testid="settings-save">
          <Save className="w-4 h-4 me-2" />{t("save")}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general" data-testid="settings-tab-general"><ImageIcon className="w-4 h-4 me-2"/>{t("st_general")}</TabsTrigger>
          <TabsTrigger value="email" data-testid="settings-tab-email"><Mail className="w-4 h-4 me-2"/>{t("st_email")}</TabsTrigger>
          <TabsTrigger value="sms" data-testid="settings-tab-sms"><MessageSquare className="w-4 h-4 me-2"/>{t("st_sms")}</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="settings-tab-whatsapp"><MessageCircle className="w-4 h-4 me-2"/>{t("st_whatsapp")}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-tab-templates"><FileText className="w-4 h-4 me-2"/>{t("st_message_templates")}</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai"><Sparkles className="w-4 h-4 me-2"/>{t("st_ai")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments"><CreditCard className="w-4 h-4 me-2"/>{t("st_payments")}</TabsTrigger>
          <TabsTrigger value="maps" data-testid="settings-tab-maps"><MapPinned className="w-4 h-4 me-2"/>{t("st_maps")}</TabsTrigger>
          {user?.role === "super_admin" && (
            <TabsTrigger value="backups" data-testid="settings-tab-backups"><Database className="w-4 h-4 me-2"/>{t("backups")}</TabsTrigger>
          )}
          {user?.role === "super_admin" && (
            <TabsTrigger value="danger" data-testid="settings-tab-danger" className="text-destructive"><AlertCircle className="w-4 h-4 me-2"/>{t("danger_zone")}</TabsTrigger>
          )}
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <Card className="p-6 border shadow-none space-y-5">
            <div>
              <Label>{t("set_logo")}</Label>
              <div className="flex items-center gap-4 mt-2">
                {v("site_logo_id") ? (
                  <img src={`${api.defaults.baseURL}/files/${v("site_logo_id")}`} alt="logo" className="w-16 h-16 object-contain rounded-md border border-border bg-muted" crossOrigin="anonymous" />
                ) : (
                  <div className="w-16 h-16 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>
                )}
                <input type="file" accept="image/*" onChange={uploadLogo} disabled={uploading} className="text-sm" data-testid="settings-logo-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>{t("set_site_name_en")}</Label><Input value={v("site_name")} onChange={(e) => merge({ site_name: e.target.value })} data-testid="settings-site-name"/></div>
              <div><Label>{t("set_site_name_ar")}</Label><Input value={v("site_name_ar")} onChange={(e) => merge({ site_name_ar: e.target.value })}/></div>
            </div>
            <div><Label>{t("set_tagline")}</Label><Input value={v("site_tagline")} onChange={(e) => merge({ site_tagline: e.target.value })} data-testid="settings-tagline"/></div>
            <div>
              <Label className="flex items-center gap-2"><Video className="w-4 h-4"/>{t("set_default_meeting_platform")}</Label>
              <Select value={v("default_meeting_platform")} onValueChange={(val) => merge({ default_meeting_platform: val })}>
                <SelectTrigger className="mt-1" data-testid="settings-meeting-platform"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="other">{t("set_other")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t("set_meeting_platform_help")}</p>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Video className="w-4 h-4"/>{t("set_meeting_url_label")} <span className="text-xs text-muted-foreground">{t("set_meeting_url_optional")}</span></Label>
              <Input
                type="url"
                placeholder="https://meet.google.com/abc-defg-hij"
                value={v("default_meeting_url")}
                onChange={(e) => merge({ default_meeting_url: e.target.value })}
                className="mt-1"
                data-testid="settings-meeting-url"
              />
              <p className="text-xs text-muted-foreground mt-1">{t("set_meeting_url_help")}</p>
            </div>
          </Card>
        </TabsContent>

        {/* Email */}
        <TabsContent value="email">
          <Card className="p-6 border shadow-none space-y-6">
            <StatusBanner label="SendGrid" configured={data.sendgrid_api_key_set} />
            <div>
              <Label>{t("set_sendgrid_key")}</Label>
              <Input
                type="password"
                placeholder={data.sendgrid_api_key_masked || "SG.xxx..."}
                value={v("sendgrid_api_key") || ""}
                onChange={(e) => merge({ sendgrid_api_key: e.target.value })}
                autoComplete="off"
                data-testid="settings-sendgrid-key"
              />
              <p className="text-xs text-muted-foreground mt-1">{t("set_stored_encrypted")}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>{t("set_from_email")}</Label><Input type="email" value={v("sendgrid_from_email")} onChange={(e) => merge({ sendgrid_from_email: e.target.value })} placeholder="no-reply@yourschool.com" data-testid="settings-sendgrid-from-email"/></div>
              <div><Label>{t("set_from_name")}</Label><Input value={v("sendgrid_from_name")} onChange={(e) => merge({ sendgrid_from_name: e.target.value })}/></div>
            </div>

            {/* --- SMTP fallback section --- */}
            <div className="border-t border-border pt-6 space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="font-semibold">{t("set_smtp_title")}</h3>
                  <p className="text-xs text-muted-foreground">{t("set_smtp_desc")}</p>
                </div>
                <div className="ms-auto"><StatusBanner label="SMTP" configured={data.smtp_configured} /></div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>{t("set_smtp_host")}</Label>
                  <Input placeholder="smtp.gmail.com" value={v("smtp_host")} onChange={(e) => merge({ smtp_host: e.target.value })} data-testid="settings-smtp-host" />
                </div>
                <div>
                  <Label>{t("set_smtp_port")}</Label>
                  <Input type="number" placeholder="587" value={v("smtp_port") || ""} onChange={(e) => merge({ smtp_port: Number(e.target.value) || 587 })} data-testid="settings-smtp-port" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("set_smtp_user")}</Label>
                  <Input placeholder="user@gmail.com" value={v("smtp_user")} onChange={(e) => merge({ smtp_user: e.target.value })} autoComplete="off" data-testid="settings-smtp-user" />
                </div>
                <div>
                  <Label>{t("set_smtp_pass")}</Label>
                  <Input type="password" placeholder={data.smtp_pass_masked || "•••"} value={v("smtp_pass") || ""} onChange={(e) => merge({ smtp_pass: e.target.value })} autoComplete="off" data-testid="settings-smtp-pass" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("set_from_email")}</Label>
                  <Input type="email" placeholder="no-reply@yourschool.com" value={v("smtp_from_email")} onChange={(e) => merge({ smtp_from_email: e.target.value })} data-testid="settings-smtp-from-email" />
                </div>
                <div>
                  <Label>{t("set_from_name")}</Label>
                  <Input value={v("smtp_from_name") || ""} onChange={(e) => merge({ smtp_from_name: e.target.value })} placeholder="SCHOOLIN AI" />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={v("smtp_use_tls") !== false}
                  onChange={(e) => merge({ smtp_use_tls: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="settings-smtp-tls"
                />
                <span className="text-sm">{t("set_smtp_tls")}</span>
              </label>

              <details className="text-xs text-muted-foreground bg-muted/40 p-3 rounded-md">
                <summary className="cursor-pointer font-medium">{t("set_gmail_quick")}</summary>
                <div className="mt-2 space-y-1">
                  <p>Host: <code className="bg-background px-1">smtp.gmail.com</code> · Port: <code className="bg-background px-1">587</code> · TLS: ✅</p>
                  <p>User: Gmail</p>
                  <p>Password: {t("set_app_password_note")} <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary underline">myaccount.google.com/apppasswords</a></p>
                </div>
              </details>

              {/* Test email */}
              <div className="bg-primary/5 border border-primary/30 rounded-lg p-3">
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label>{t("set_test_email_to")}</Label>
                    <Input
                      type="email"
                      placeholder="test@example.com"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                      data-testid="settings-test-email-to"
                    />
                  </div>
                  <Button onClick={sendTestEmail} disabled={testingEmail || !testEmailTo} data-testid="settings-test-email-btn">
                    {testingEmail ? "..." : t("set_test_send_btn")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{t("set_test_email_note")}</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* SMS — Multi-provider (Twilio + Gulf/Saudi providers) */}
        <TabsContent value="sms">
          <Card className="p-6 border shadow-none space-y-5" data-testid="settings-sms-card">
            {(() => {
              const activeProvider = v("sms_provider") || "twilio";
              const providerCfg = smsProviders.find(p => p.id === activeProvider);
              const configured = (() => {
                if (activeProvider === "twilio") return data.twilio_account_sid_set && data.twilio_auth_token_set;
                if (activeProvider === "unifonic") return data.unifonic_app_sid_set;
                if (activeProvider === "taqnyat") return data.taqnyat_bearer_set;
                if (activeProvider === "jawaly") return data.jawaly_api_key_set && data.jawaly_api_secret_set;
                if (activeProvider === "msegat") return data.msegat_api_key_set;
                if (activeProvider === "generic_http") return !!data.generic_sms_url;
                return false;
              })();
              return (
                <>
                  <StatusBanner label={providerCfg?.name_ar || providerCfg?.name || activeProvider} configured={configured} />

                  {/* Provider selector */}
                  <div>
                    <Label>{t("set_sms_provider_label")}</Label>
                    <Select value={activeProvider} onValueChange={(val) => merge({ sms_provider: val })}>
                      <SelectTrigger className="mt-1" data-testid="settings-sms-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {smsProviders.map(p => (
                          <SelectItem key={p.id} value={p.id} data-testid={`sms-provider-${p.id}`}>
                            <span className="flex items-center gap-2">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{p.country}</span>
                              {p.name_ar} · {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("set_sms_provider_help")}
                    </p>
                  </div>

                  {/* Dynamic provider-specific fields */}
                  {providerCfg && (
                    <div className="grid sm:grid-cols-2 gap-4 border-t pt-4">
                      {providerCfg.fields.map((f) => {
                        const maskedKey = `${f.key}_masked`;
                        const setKey = `${f.key}_set`;
                        const isSecret = f.secret || f.type === "password";
                        const isTextarea = f.type === "textarea";
                        const placeholder = isSecret && data[maskedKey]
                          ? data[maskedKey]
                          : (f.placeholder || "");
                        return (
                          <div key={f.key} className={isTextarea ? "sm:col-span-2" : ""}>
                            <Label>{f.label}</Label>
                            {isTextarea ? (
                              <textarea
                                value={v(f.key) || ""}
                                onChange={(e) => merge({ [f.key]: e.target.value })}
                                placeholder={placeholder}
                                rows={3}
                                className="mt-1 w-full font-mono text-xs px-3 py-2 border rounded-md bg-background"
                                data-testid={`settings-sms-${f.key}`}
                              />
                            ) : (
                              <Input
                                type={f.type === "password" ? "password" : "text"}
                                value={v(f.key) || ""}
                                onChange={(e) => merge({ [f.key]: e.target.value })}
                                placeholder={placeholder}
                                autoComplete="off"
                                data-testid={`settings-sms-${f.key}`}
                              />
                            )}
                            {isSecret && data[setKey] && (
                              <p className="text-[10px] text-emerald-600 mt-1">{t("set_sms_saved_keep")}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Test SMS */}
                  <div className="border-t pt-4">
                    <Label>{t("set_sms_test_label")}</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={testSmsTo}
                        onChange={(e) => setTestSmsTo(e.target.value)}
                        placeholder="+966500000000"
                        dir="ltr"
                        data-testid="settings-test-sms-to"
                      />
                      <Button
                        type="button"
                        onClick={sendTestSms}
                        disabled={testingSms || !configured}
                        variant="outline"
                        data-testid="settings-test-sms-btn"
                      >
                        {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : t("set_sms_test_btn")}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("set_sms_current_provider")} <b>{providerCfg?.name_ar || activeProvider}</b>.
                    </p>
                  </div>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        {/* WhatsApp — Multi-provider (Meta Cloud FREE tier + Twilio + UltraMsg + 360dialog + Wati) */}
        <TabsContent value="whatsapp">
          <Card className="p-6 border shadow-none space-y-5" data-testid="settings-whatsapp-card">
            {(() => {
              const activeProvider = v("wa_provider") || "";
              const providerCfg = waProviders.find(p => p.id === activeProvider);
              const configured = (() => {
                if (activeProvider === "meta_cloud") return data.wa_meta_access_token_set && !!data.wa_meta_phone_number_id;
                if (activeProvider === "twilio_wa") return data.wa_twilio_account_sid_set && data.wa_twilio_auth_token_set && !!data.wa_twilio_from;
                if (activeProvider === "ultramsg") return !!data.wa_ultramsg_instance_id && data.wa_ultramsg_token_set;
                if (activeProvider === "dialog360") return data.wa_dialog360_api_key_set;
                if (activeProvider === "wati") return !!data.wa_wati_tenant_id && data.wa_wati_access_token_set;
                if (activeProvider === "generic_http") return !!data.wa_generic_url;
                return false;
              })();
              return (
                <>
                  <div className="rounded-lg p-3 border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30 flex gap-3 items-start">
                    <MessageCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-semibold text-emerald-800 dark:text-emerald-200">اختَر مزود واتساب مناسب لدولتك أو ميزانيتك</div>
                      <div className="text-xs text-emerald-800/80 dark:text-emerald-300/80 mt-1">
                        <b>مجاناً:</b> Meta WhatsApp Cloud API — ~1000 محادثة/شهر مجاناً (رسمي من ميتا).
                        الباقي مدفوع (Twilio عالمي · UltraMsg اقتصادي · 360dialog و Wati للأعمال).
                      </div>
                    </div>
                  </div>

                  <StatusBanner label={providerCfg?.name_ar || providerCfg?.name || "WhatsApp"} configured={configured} />

                  {/* Provider selector */}
                  <div>
                    <Label>اختر مزود واتساب</Label>
                    <Select value={activeProvider || "meta_cloud"} onValueChange={(val) => merge({ wa_provider: val })}>
                      <SelectTrigger className="mt-1" data-testid="settings-wa-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {waProviders.map(p => (
                          <SelectItem key={p.id} value={p.id} data-testid={`wa-provider-${p.id}`}>
                            <span className="flex items-center gap-2">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{p.country}</span>
                              {p.free_tier && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">مجاني</span>}
                              {p.name_ar} · {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {providerCfg?.free_tier && (
                      <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-2 font-semibold">
                        ✓ طبقة مجانية: {providerCfg.free_tier}
                      </p>
                    )}
                  </div>

                  {/* Dynamic provider-specific fields */}
                  {providerCfg && (
                    <div className="grid sm:grid-cols-2 gap-4 border-t pt-4">
                      {providerCfg.fields.map((f) => {
                        const maskedKey = `${f.key}_masked`;
                        const setKey = `${f.key}_set`;
                        const isSecret = f.secret || f.type === "password";
                        const isTextarea = f.type === "textarea";
                        const placeholder = isSecret && data[maskedKey]
                          ? data[maskedKey]
                          : (f.placeholder || "");
                        return (
                          <div key={f.key} className={isTextarea ? "sm:col-span-2" : ""}>
                            <Label>{f.label}</Label>
                            {isTextarea ? (
                              <textarea
                                value={v(f.key) || ""}
                                onChange={(e) => merge({ [f.key]: e.target.value })}
                                placeholder={placeholder}
                                rows={3}
                                className="mt-1 w-full font-mono text-xs px-3 py-2 border rounded-md bg-background"
                                data-testid={`settings-wa-${f.key}`}
                              />
                            ) : (
                              <Input
                                type={f.type === "password" ? "password" : "text"}
                                value={v(f.key) || ""}
                                onChange={(e) => merge({ [f.key]: e.target.value })}
                                placeholder={placeholder}
                                autoComplete="off"
                                data-testid={`settings-wa-${f.key}`}
                              />
                            )}
                            {isSecret && data[setKey] && (
                              <p className="text-[10px] text-emerald-600 mt-1">تم الحفظ — اترك الحقل فارغاً للإبقاء عليه</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Test WhatsApp */}
                  <div className="border-t pt-4">
                    <Label>إرسال اختبار</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={testWaTo}
                        onChange={(e) => setTestWaTo(e.target.value)}
                        placeholder="+966500000000"
                        dir="ltr"
                        data-testid="settings-test-wa-to"
                      />
                      <Button
                        type="button"
                        onClick={sendTestWhatsapp}
                        disabled={testingWa || !configured}
                        variant="outline"
                        data-testid="settings-test-wa-btn"
                      >
                        {testingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : "إرسال"}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      المزود الحالي: <b>{providerCfg?.name_ar || activeProvider || "لم يُختر"}</b>. احفظ الإعدادات أولاً ثم اختبر.
                    </p>
                  </div>

                  {/* WhatsApp notification types */}
                  <div className="border-t pt-4">
                    <Label className="text-base">أنواع الإشعارات المرسلة عبر واتساب</Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">
                      حدّد الإشعارات التي تُرسل تلقائياً لأولياء الأمور عبر واتساب (الافتراضي: الكل مفعّل)
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {WA_NOTIFY_TYPES.map(nt => {
                        const cur = { ...(data.wa_notify_types || {}), ...(form.wa_notify_types || {}) };
                        const enabled = cur[nt.key] !== false;
                        return (
                          <label key={nt.key} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/30 smooth" data-testid={`wa-notify-${nt.key}`}>
                            <span className="text-sm">{nt.label}</span>
                            <Switch checked={enabled}
                                    onCheckedChange={(val) => merge({ wa_notify_types: { ...cur, [nt.key]: val } })}
                                    data-testid={`wa-notify-switch-${nt.key}`} />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        {/* AI */}
        {/* Message templates (SMS / WhatsApp bodies) */}
        <TabsContent value="templates">
          <MessageTemplatesTab />
        </TabsContent>

        <TabsContent value="ai">
          <div className="space-y-4">
            {/* New: Per-school AI Provider selector */}
            <AiProviderCard schoolId={school?.id} />

            {/* Daily Notes settings (kept from before) */}
          <Card className="p-6 border shadow-none space-y-5">
            <div className="border-t border-border pt-5 space-y-3">
              <div className="text-sm font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center text-white">
                  <Sparkles className="w-3.5 h-3.5" />
                </span>
                {t("daily_notes_settings")}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!v("daily_notes_enabled")}
                  onChange={(e) => merge({ daily_notes_enabled: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                  data-testid="settings-daily-notes-enable"
                />
                <span className="text-sm">{t("daily_notes_enable_label")}</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3 items-end">
                <div>
                  <Label>{t("daily_notes_time_label")}</Label>
                  <Input
                    type="time"
                    value={v("daily_notes_time") || "20:00"}
                    onChange={(e) => merge({ daily_notes_time: e.target.value })}
                    disabled={!v("daily_notes_enabled")}
                    data-testid="settings-daily-notes-time"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("daily_notes_time_help")}</p>
            </div>
          </Card>
          </div>
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments">
          <Card className="p-6 border shadow-none space-y-5">
            <StatusBanner label={t("set_pay_gateway")} configured={!!data.payment_configured} />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("set_pay_provider")}</Label>
                <Select value={v("payment_provider") || "none"} onValueChange={(val) => merge({ payment_provider: val })}>
                  <SelectTrigger className="mt-1" data-testid="settings-payment-provider"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("set_pay_none")}</SelectItem>
                    <SelectItem value="stripe">{t("set_pay_stripe")}</SelectItem>
                    <SelectItem value="mada">{t("set_pay_mada")}</SelectItem>
                    <SelectItem value="paymob">{t("set_pay_paymob") || "Paymob (EGY / KSA / UAE / OMN)"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("currency")}</Label>
                <Select value={v("payment_currency") || "SAR"} onValueChange={(val) => merge({ payment_currency: val })}>
                  <SelectTrigger className="mt-1" data-testid="settings-payment-currency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">{t("set_currency_sar")}</SelectItem>
                    <SelectItem value="AED">{t("set_currency_aed")}</SelectItem>
                    <SelectItem value="EGP">{t("set_currency_egp") || "EGP — جنيه مصري"}</SelectItem>
                    <SelectItem value="OMR">{t("set_currency_omr") || "OMR — ريال عُماني"}</SelectItem>
                    <SelectItem value="USD">{t("set_currency_usd")}</SelectItem>
                    <SelectItem value="EUR">{t("set_currency_eur")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Stripe section */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="text-sm font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4"/>{t("set_stripe_creds_title")}</div>
              <p className="text-xs text-muted-foreground">{t("set_stripe_creds_desc")}</p>
              <div>
                <Label>{t("set_stripe_secret")}</Label>
                <Input type="password" placeholder={data.stripe_secret_key_masked || "sk_test_..."} value={v("stripe_secret_key") || ""} onChange={(e) => merge({ stripe_secret_key: e.target.value })} autoComplete="off" data-testid="settings-stripe-secret"/>
              </div>
              <div>
                <Label>{t("set_stripe_public")}</Label>
                <Input placeholder="pk_test_..." value={v("stripe_publishable_key") || ""} onChange={(e) => merge({ stripe_publishable_key: e.target.value })} autoComplete="off" data-testid="settings-stripe-pk"/>
              </div>
              <div>
                <Label>{t("set_stripe_webhook")}</Label>
                <Input type="password" placeholder={data.stripe_webhook_secret_masked || "whsec_..."} value={v("stripe_webhook_secret") || ""} onChange={(e) => merge({ stripe_webhook_secret: e.target.value })} autoComplete="off" data-testid="settings-stripe-whsec"/>
              </div>
            </div>

            {/* Mada section */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="text-sm font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4"/>{t("set_mada_creds_title")}</div>
              <p className="text-xs text-muted-foreground">{t("set_mada_creds_desc")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("set_mada_merchant")}</Label>
                  <Input placeholder={data.mada_merchant_id || "merchant-id"} value={v("mada_merchant_id") || ""} onChange={(e) => merge({ mada_merchant_id: e.target.value })} data-testid="settings-mada-merchant"/>
                </div>
                <div>
                  <Label>{t("set_mada_terminal")}</Label>
                  <Input placeholder={data.mada_terminal_id || "terminal-id"} value={v("mada_terminal_id") || ""} onChange={(e) => merge({ mada_terminal_id: e.target.value })} data-testid="settings-mada-terminal"/>
                </div>
              </div>
              <div>
                <Label>{t("set_mada_api")}</Label>
                <Input type="password" placeholder={data.mada_api_key_masked || "•••"} value={v("mada_api_key") || ""} onChange={(e) => merge({ mada_api_key: e.target.value })} autoComplete="off" data-testid="settings-mada-api-key"/>
              </div>
            </div>

            {/* Paymob section (multi-country: EGY / KSA / UAE / OMN) */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="text-sm font-semibold flex items-center gap-2">
                <CreditCard className="w-4 h-4"/>{t("set_paymob_creds_title") || "Paymob — مزود دفع متعدد الدول"}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("set_paymob_creds_desc") || "يدعم: مصر (EGP)، السعودية (SAR)، الإمارات (AED)، عُمان (OMR). يُفعَّل عند اختيار Paymob كمزود الدفع أعلاه."}
              </p>
              <div>
                <Label>{t("set_paymob_region") || "الدولة / Region"}</Label>
                <Select value={v("paymob_region") || "ksa"} onValueChange={(val) => merge({ paymob_region: val })}>
                  <SelectTrigger className="mt-1" data-testid="settings-paymob-region"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ksa">السعودية — KSA (ksa.paymob.com)</SelectItem>
                    <SelectItem value="egy">مصر — Egypt (accept.paymob.com)</SelectItem>
                    <SelectItem value="uae">الإمارات — UAE (uae.paymob.com)</SelectItem>
                    <SelectItem value="omn">عُمان — Oman (oman.paymob.com)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("set_paymob_secret") || "المفتاح السري (Secret Key)"}</Label>
                <Input
                  type="password"
                  placeholder={data.paymob_secret_key_masked || "sk_test_... أو sk_live_..."}
                  value={v("paymob_secret_key") || ""}
                  onChange={(e) => merge({ paymob_secret_key: e.target.value })}
                  autoComplete="off"
                  data-testid="settings-paymob-secret"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("set_paymob_secret_help") || "من Paymob Dashboard → Developers → Payment Integrations"}
                </p>
              </div>
              <div>
                <Label>{t("set_paymob_public") || "المفتاح العام (Public Key)"}</Label>
                <Input
                  placeholder={data.paymob_public_key || "pk_test_..."}
                  value={v("paymob_public_key") || ""}
                  onChange={(e) => merge({ paymob_public_key: e.target.value })}
                  autoComplete="off"
                  data-testid="settings-paymob-public"
                />
              </div>
              <div>
                <Label>{t("set_paymob_integration_ids") || "Integration IDs"}</Label>
                <Input
                  placeholder={data.paymob_integration_ids || "1256, 4345907"}
                  value={v("paymob_integration_ids") || ""}
                  onChange={(e) => merge({ paymob_integration_ids: e.target.value })}
                  data-testid="settings-paymob-integration-ids"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("set_paymob_integration_help") || "أرقام طرق الدفع المفعّلة (Card / Wallet / Mada …) مفصولة بفواصل."}
                </p>
              </div>
              <div>
                <Label>{t("set_paymob_hmac") || "HMAC Secret (للتحقق من webhook)"}</Label>
                <Input
                  type="password"
                  placeholder={data.paymob_hmac_secret_masked || "•••"}
                  value={v("paymob_hmac_secret") || ""}
                  onChange={(e) => merge({ paymob_hmac_secret: e.target.value })}
                  autoComplete="off"
                  data-testid="settings-paymob-hmac"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("set_paymob_hmac_help") || "اختياري لكن موصى به للإنتاج. يُستخدم للتحقق من توقيع كل إشعار دفع."}
                </p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-t border-border pt-3">
              {t("set_pay_save_note")}
            </p>
          </Card>
        </TabsContent>

        {/* Maps */}
        <TabsContent value="maps">
          <Card className="p-6 border shadow-none space-y-5">
            <StatusBanner label={t("set_maps_provider_status")} configured={!!data.google_maps_api_key_set || !!data.mapbox_token_set || data.maps_provider === "openstreetmap"} />

            <div>
              <Label>{t("set_maps_provider_label")}</Label>
              <Select value={v("maps_provider") || "google"} onValueChange={(val) => merge({ maps_provider: val })}>
                <SelectTrigger className="mt-1" data-testid="settings-maps-provider"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">{t("set_maps_google")}</SelectItem>
                  <SelectItem value="openstreetmap">{t("set_maps_osm")}</SelectItem>
                  <SelectItem value="mapbox">Mapbox</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t("set_maps_used_for")}</p>
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <div className="text-sm font-semibold flex items-center gap-2"><MapPinned className="w-4 h-4"/>Google Maps</div>
              <p className="text-xs text-muted-foreground">{t("set_maps_google_help")}</p>
              <div>
                <Label>{t("set_maps_google_api")}</Label>
                <Input
                  type="password"
                  placeholder={data.google_maps_api_key_masked || "AIzaSy..."}
                  value={v("google_maps_api_key") || ""}
                  onChange={(e) => merge({ google_maps_api_key: e.target.value })}
                  autoComplete="off"
                  data-testid="settings-google-maps-key"
                />
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <div className="text-sm font-semibold flex items-center gap-2"><MapPinned className="w-4 h-4"/>{t("set_maps_mapbox_title")}</div>
              <div>
                <Label>{t("set_maps_mapbox_token")}</Label>
                <Input
                  type="password"
                  placeholder={data.mapbox_token_masked || "pk.eyJ..."}
                  value={v("mapbox_token") || ""}
                  onChange={(e) => merge({ mapbox_token: e.target.value })}
                  autoComplete="off"
                  data-testid="settings-mapbox-token"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-t border-border pt-3">
              {t("set_maps_fallback_note")}
            </p>
          </Card>
        </TabsContent>

        {/* Smart Check-in — moved to per-school configuration */}
        <TabsContent value="checkin">
          <Card className="p-6 border shadow-none space-y-5">
            <div className="flex items-start gap-3 px-4 py-3 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <div className="font-semibold mb-1">{t("set_checkin_moved_title")}</div>
                <p>{t("set_checkin_moved_desc")}</p>
                <p className="mt-2 text-xs">{t("set_checkin_auto_use")}</p>
              </div>
            </div>
            <Button asChild data-testid="settings-goto-schools">
              <a href="/schools"><MapPinned className="w-4 h-4 me-2" />{t("set_goto_schools")}</a>
            </Button>
            <div className="text-xs text-muted-foreground border-t border-border pt-3 leading-relaxed">
              {t("set_checkin_admin_help")}
            </div>
          </Card>
        </TabsContent>

        {/* BACKUPS — super_admin only */}
        {user?.role === "super_admin" && (
          <TabsContent value="backups">
            <Card className="p-6 border shadow-none space-y-5" data-testid="backups-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-bold flex items-center gap-2"><Database className="w-5 h-5 text-primary"/>{t("backups")}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{t("backup_keeps_n")}</p>
                  {backupStatus && (
                    <div className="mt-2 text-xs">
                      {backupStatus.s3_enabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 font-medium">
                          ☁️ {t("set_backup_cloud_msg")} ({backupStatus.s3_bucket})
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 font-medium">
                          {t("set_backup_cloud_off")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Button onClick={createBackupNow} disabled={backupBusy} data-testid="backup-now-btn">
                  {backupBusy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Database className="w-4 h-4 me-2" />}
                  {t("backup_now")}
                </Button>
              </div>

              {backups.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-md">
                  {t("no_backups_yet")}
                </div>
              ) : (
                <div className="border rounded-md divide-y divide-border bg-background overflow-hidden">
                  {backups.map((b) => (
                    <div key={b.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`backup-${b.id}`}>
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-medium truncate">{b.filename}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(b.created_at).toLocaleString("ar")} · {(b.size_bytes / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.id}`}>
                          <Download className="w-4 h-4 me-1" />{t("backup_download")}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => restoreBackup(b)} disabled={backupBusy} className="text-amber-700 border-amber-500/40" data-testid={`backup-restore-${b.id}`}>
                          <RotateCcw className="w-4 h-4 me-1" />{t("backup_restore")}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteBackup(b)} className="text-destructive hover:bg-destructive/10" data-testid={`backup-delete-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}

        {/* DANGER ZONE — super_admin only: granular admin actions */}
        {user?.role === "super_admin" && (
          <TabsContent value="danger">
            <div className="space-y-4">
              {/* User Management */}
              <Card className="p-6 border-2 border-amber-500/40 bg-amber-500/5 shadow-none space-y-4" data-testid="user-mgmt-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 shrink-0 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-700 dark:text-amber-400">{t("set_user_mgmt_title")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t("set_user_mgmt_count_a")} <b>{allUsers.length}</b> {t("set_user_mgmt_count_b")} <b>{allUsers.filter(u => u.role === "super_admin").length}</b> {t("set_user_mgmt_count_c")}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={cleanupDemoUsers} data-testid="cleanup-demo-users-btn" className="text-destructive border-destructive/40">
                    {t("set_cleanup_demo_btn")}
                  </Button>
                </div>
                <div className="border rounded-md divide-y divide-border bg-background max-h-72 overflow-y-auto">
                  {allUsers.map(u => {
                    const isSelf = u.id === user.id;
                    const isSeeded = u.seeded === true;
                    return (
                      <div key={u.id} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`admin-user-${u.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${u.role === "super_admin" ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>{u.role}</span>
                          <span className="font-medium truncate">{u.name || "—"}</span>
                          <span className="text-xs text-muted-foreground truncate">· {u.email}</span>
                          {isSeeded && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300">{t("set_label_demo")}</span>}
                          {isSelf && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">{t("set_label_you")}</span>}
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => deleteOneUser(u)}
                          disabled={isSelf}
                          data-testid={`del-user-${u.id}`}
                          className="text-destructive hover:bg-destructive/10"
                        >{t("set_del_btn")}</Button>
                      </div>
                    );
                  })}
                  {allUsers.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t("set_no_users")}</div>}
                </div>
              </Card>

              {/* Audit logs */}
              <Card className="p-6 border-2 border-blue-500/40 bg-blue-500/5 shadow-none space-y-3" data-testid="audit-clear-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 shrink-0 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-700 dark:text-blue-400">{t("set_audit_title")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t("set_audit_desc")}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={clearAuditLogs} data-testid="clear-audit-btn">
                    {t("set_audit_clear_btn")}
                  </Button>
                </div>
              </Card>

              {/* Transport logs */}
              <Card className="p-6 border-2 border-violet-500/40 bg-violet-500/5 shadow-none space-y-3" data-testid="transport-clear-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 shrink-0 text-violet-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-violet-700 dark:text-violet-400">{t("set_transport_logs_title")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t("set_transport_logs_desc")}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={clearTransportLogs} data-testid="clear-transport-btn">
                    {t("set_transport_logs_btn")}
                  </Button>
                </div>
              </Card>

              {/* Full reset */}
              <Card className="p-6 border-2 border-destructive/40 bg-destructive/5 shadow-none space-y-5" data-testid="danger-zone-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 shrink-0 text-destructive mt-0.5" />
                  <div>
                    <h3 className="font-bold text-destructive">{t("danger_zone")} — {t("set_reset_full_title")}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      {t("set_reset_warning")}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg bg-background border border-border p-4 space-y-2 text-sm">
                  <div className="font-semibold mb-2">{t("set_reset_kept_title")}</div>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>{t("set_reset_kept_users")}</li>
                    <li>{t("set_reset_kept_depts")}</li>
                    <li>{t("set_reset_kept_settings")}</li>
                  </ul>
                  <div className="border-t border-border my-3" />
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={keepSchools} onChange={(e) => setKeepSchools(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="reset-keep-schools" />
                    <span>{t("set_reset_keep_schools")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={keepUploadedFiles} onChange={(e) => setKeepUploadedFiles(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="reset-keep-files" />
                    <span>{t("set_reset_keep_files")}</span>
                  </label>
                </div>

                <div>
                  <Label className="text-destructive">{t("set_reset_confirm_label")} <code className="font-mono bg-destructive/10 px-2 py-0.5 rounded">RESET ALL DATA</code></Label>
                  <Input
                    value={resetPhrase}
                    onChange={(e) => setResetPhrase(e.target.value)}
                    placeholder="RESET ALL DATA"
                    dir="ltr"
                    className="mt-1 font-mono"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  variant="destructive"
                  onClick={performReset}
                  disabled={resetting || resetPhrase !== "RESET ALL DATA"}
                  className="w-full"
                  data-testid="reset-data-btn"
                >
                  {resetting ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <AlertCircle className="w-4 h-4 me-2" />}
                  {resetting ? t("set_resetting") : t("set_reset_btn")}
                </Button>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function StatusBanner({ label, configured }) {
  if (configured) {
    return (
      <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-primary/10 text-primary border border-primary/30">
        <CheckCircle2 className="w-4 h-4" /> <span><b>{label}</b> <ConfiguredLabel /></span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
      <AlertCircle className="w-4 h-4" /> <span><b>{label}</b> <NotConfiguredLabel /></span>
    </div>
  );
}

function ConfiguredLabel() {
  const { t } = useI18n();
  return <>{t("set_configured_active")}</>;
}

function NotConfiguredLabel() {
  const { t } = useI18n();
  return <>{t("set_not_configured")}</>;
}
